import {
  computeButtonFormatting,
  computeIsSelectionOnlyDatetimeCells,
  computeLinkButtonDisabled,
  computeSelectionAlignment,
  computeSelectionLink,
  computeSelectionPrecision,
  MergeButtonFormatting,
  SelectionLink,
} from 'notebook/tables/store/logic/TableActiveCellLogic';
import {TableSelectionModel} from 'notebook/tables/store/selectors/tableModel/TableSelectionSelectors';
import {createTableStoreSelector} from 'notebook/tables/store/selectors/util/SelectorCreators';
import {TableEditableStore, TableEditableVolatileProps} from 'notebook/tables/store/TableStore';
import TableUtil from 'notebook/tables/TableUtil';
import {AlignmentState} from 'notebook/tables/toolbar/FormulaBox';
import {Optional} from 'util/Types';

/**
 * Model containing formula-bar / table-controls state for the currently active
 * cell.
 *
 */
export interface TableActiveCellModel {
  /**
   * Whether the "add link" button is disabled. A link may only be set when
   * exactly one cell is selected.
   */
  linkButtonDisabled: boolean;

  /**
   * The link (url + text) of the active cell, or an empty object when there is
   * no linkable selection.
   */
  link: SelectionLink;

  /**
   * The shared precision across all rendered selected cells, or `null` when
   * there is no selection or the selected cells have differing precisions.
   */
  precision: Optional<number>;

  /**
   * The alignment state (explicit `active` alignment and rendered `default`
   * alignment) for the current selection.
   */
  alignment: AlignmentState;

  /**
   * Whether the current selection contains only datetime cells. Used to gate
   * the "insert now" datetime action in the table controls.
   */
  isDatetimeCell: boolean;

  /**
   * The state (`active` / `disabled`) of the "merge cells" button in the table
   * controls for the current selection.
   */
  buttonFormatting: MergeButtonFormatting;
}

/**
 * Creates a memoized selector that computes the TableActiveCellModel from the store.
 */
export function createTableActiveCellSelector(
  volatilePropsSelector: (store: TableEditableStore) => TableEditableVolatileProps,
  tableSelectionSelector: (store: TableEditableStore) => TableSelectionModel,
  boundaryIndexSelector: (store: TableEditableStore) => number | null
): (store: TableEditableStore) => TableActiveCellModel {
  return createTableStoreSelector(
    {
      multiStartIndex: (store) => store.state.multiStartIndex,
      multiEndIndex: (store) => store.state.multiEndIndex,
      table: (store) => volatilePropsSelector(store).table,
      computedTable: (store) => volatilePropsSelector(store).computedTable,
      hasRealSelection: (store) => tableSelectionSelector(store).hasRealSelection,
      expandedBox: (store) => tableSelectionSelector(store).expandedBox,
      singleInputBoundaryIndex: boundaryIndexSelector,
      allowMergeSelection: (store) => store.stableProps.allowMergeSelection,
    },
    ({
      multiStartIndex,
      multiEndIndex,
      table,
      computedTable,
      hasRealSelection,
      expandedBox,
      singleInputBoundaryIndex,
      allowMergeSelection,
    }) => {
      const linkButtonDisabled = computeLinkButtonDisabled(multiStartIndex, multiEndIndex);
      return {
        linkButtonDisabled,
        link: computeSelectionLink(hasRealSelection, linkButtonDisabled, multiStartIndex, table),
        precision: computeSelectionPrecision(table, computedTable, expandedBox, hasRealSelection),
        alignment: computeSelectionAlignment(table, computedTable, expandedBox, hasRealSelection),
        isDatetimeCell: computeIsSelectionOnlyDatetimeCells(
          table.columnConstraints,
          expandedBox,
          hasRealSelection
        ),
        buttonFormatting: computeButtonFormatting(
          hasRealSelection,
          multiStartIndex,
          multiEndIndex,
          singleInputBoundaryIndex,
          TableUtil.getFreezeBoundary(table),
          computedTable,
          allowMergeSelection ?? undefined
        ),
      };
    }
  );
}
